import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Effect from 'effect/Effect';
import { pipe } from 'effect/Function';
import { ApiClient } from '@/api/client';
import { MainLayer } from '@/effects/layers';
import { runEffect } from '@/effects/runtime';
import { QUERY_KEYS } from '@/lib/constants';
import { z } from 'zod';

const CreateSettlementSchema = z.object({
  from_user_id: z.string(),
  to_user_id: z.string(),
  amount: z.number(),
  currency_code: z.string(),
  description: z.string().optional(),
});

const SettlementResponseSchema = z.object({
  settlement: z.object({
    id: z.string(),
    group_id: z.string(),
    from_user_id: z.string(),
    to_user_id: z.string(),
    amount: z.number(),
    currency_code: z.string(),
    description: z.string().optional(),
    created_at: z.string(),
    created_by: z.string(),
  }),
});

type CreateSettlementInput = z.infer<typeof CreateSettlementSchema>;

export function useCreateSettlement(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateSettlementInput) => {
      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          const url = `/groups/${groupId}/settlements`;
          const response = yield* apiClient.post(url, input, SettlementResponseSchema);
          return response;
        }),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: () => {
      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRANSACTIONS, groupId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GROUP_BALANCES, groupId] });
    },
  });
}